import React from 'react';

/**
 * Red de seguridad para errores de RENDER (issue #47).
 *
 * Alcance, para que nadie le pida lo que no puede dar: un ErrorBoundary de
 * React captura excepciones lanzadas durante el render, los métodos de ciclo
 * de vida y los constructores de sus hijos. NO captura:
 *
 *   - errores al EVALUAR un módulo (los imports corren antes de que exista
 *     ningún componente) — de eso se encarga el fallback de `index.html`;
 *   - errores dentro de manejadores de eventos;
 *   - rechazos de promesas no capturados;
 *   - errores del render en servidor.
 *
 * Es decir: esto NO habría evitado la pantalla en blanco del 2026-08-29, que
 * venía de `getAuth()` tronando durante el import. Lo que la evita es el
 * guardarraíl de `vite.config.ts`. Esto cubre el otro agujero que teníamos
 * abierto: hasta hoy, CUALQUIER excepción de render dejaba la app en blanco
 * igual, porque no había un solo boundary en toda la aplicación.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Sin telemetría todavía: la consola es el único destino que existe hoy.
    // Cuando haya reporte de errores, este es el punto por el que entra.
    console.error('[ErrorBoundary] la UI falló al renderizar:', error, info.componentStack);
  }

  private reintentar = () => {
    // Recarga completa en vez de limpiar el estado: si el árbol reventó, el
    // estado de los providers (sesión, alertas, chat) puede haber quedado a
    // medias, y volver a montar sobre eso reproduce el fallo casi siempre.
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary__caja">
          <h1 className="error-boundary__titulo">Algo se rompió en la aplicación</h1>
          <p className="error-boundary__texto">
            El fallo es nuestro, no de tu conexión ni de tu sesión. Vuelve a cargar la
            página; si sigue pasando, avisa al equipo con el detalle de abajo.
          </p>
          <pre className="error-boundary__detalle">{error.message || String(error)}</pre>
          <button type="button" className="btn btn--primary" onClick={this.reintentar}>
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
